import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Container, 
  IconButton, 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Header: React.FC = () => {
  const { volunteer, isAuthenticated, isAdmin, isJobManager, isQRCodeManager, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };
  const navItems: Array<{
    label: string;
    path: string;
    requireAuth: boolean;
    requireAdmin?: boolean;
    requireJobManager?: boolean;
    requireQRCodeManager?: boolean;
  }> = [
    { label: 'Startseite', path: '/', requireAuth: false },
    { label: 'Dienste', path: '/jobs', requireAuth: false },
    // Show job management for job managers (but not if they're also admin)
    { label: 'Dienste verwalten', path: '/job-manager', requireAuth: true, requireJobManager: true },
    // Show admin job management for admins
    { label: 'Dienste verwalten', path: '/admin/job-manager', requireAuth: true, requireAdmin: true },
    { label: 'Rollen verwalten', path: '/admin/roles', requireAuth: true, requireAdmin: true },
    { label: 'QR-Codes verwalten', path: '/qr-codes', requireAuth: true, requireQRCodeManager: true },
    { label: 'QR-Scanner', path: '/qr-scanner', requireAuth: true, requireQRCodeManager: true },
    { label: 'Administration', path: '/admin', requireAuth: true, requireAdmin: true },
    { label: 'Dienstübersicht', path: '/admin/dashboard', requireAuth: true, requireAdmin: true },
    { label: 'Meine Dienstübersicht', path: '/volunteer/dashboard', requireAuth: true },
    { label: 'Mein Profil', path: '/volunteer/profile', requireAuth: true }
  ];
  return (
    <AppBar 
      position="static" 
      elevation={0}
      className="no-print"
      sx={{ 
        borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: 'primary.main',
      }}
    >
      <Container maxWidth="xl">
        <Toolbar 
          disableGutters 
          sx={{ 
            minHeight: { xs: '56px', sm: '64px' },
            py: 1,
          }}
        >
          {/* Mobile menu hamburger icon */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleMobileMenuToggle}
            sx={{ 
              mr: 2,
              display: { xs: 'flex', md: 'none' } 
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            component={RouterLink}
            to="/"
            sx={{
              mr: 4,
              display: { xs: 'flex', md: 'flex' },
              fontWeight: 700,
              letterSpacing: '.1rem',
              color: 'white',
              textDecoration: 'none',
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.9)',
              },
              // Make it centered on mobile
              flexGrow: { xs: 1, md: 0 },
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            STRUDENGAUER MESSE
          </Typography>          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 2 }}>
            {navItems.map((item, index) => {
              // Special logic for "Dienste verwalten" to avoid duplicates
              if (item.label === 'Dienste verwalten') {
                // If user is admin, only show the admin version
                if (isAdmin && item.path === '/job-manager') {
                  return null;
                }
                // If user is only job manager (not admin), only show the job manager version
                if (isJobManager && !isAdmin && item.path === '/admin/job-manager') {
                  return null;
                }
              }
              
              // Only show items that match auth status
              if ((item.requireAuth && !isAuthenticated) || 
                  (item.requireAdmin && !isAdmin) ||
                  (item.requireJobManager && !isJobManager) ||
                  (item.requireQRCodeManager && !isQRCodeManager)) {
                return null;
              }
              
              return (
                <Button
                  key={index}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'white',
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
          
          {/* Mobile Drawer Navigation */}
          <Drawer
            anchor="left"
            open={mobileMenuOpen}
            onClose={handleMobileMenuToggle}
            sx={{
              '& .MuiDrawer-paper': { 
                width: 240,
                bgcolor: 'white',
                boxShadow: 3,
              },
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                Strudengauer Messe
              </Typography>
            </Box>
            
            <Divider />
            
            <List>
              {navItems.map((item, index) => {
                // Special logic for "Dienste verwalten" to avoid duplicates
                if (item.label === 'Dienste verwalten') {
                  // If user is admin, only show the admin version
                  if (isAdmin && item.path === '/job-manager') {
                    return null;
                  }
                  // If user is only job manager (not admin), only show the job manager version
                  if (isJobManager && !isAdmin && item.path === '/admin/job-manager') {
                    return null;
                  }
                }
                
                // Only show items that match auth status
                if ((item.requireAuth && !isAuthenticated) || 
                    (item.requireAdmin && !isAdmin) ||
                    (item.requireJobManager && !isJobManager) ||
                    (item.requireQRCodeManager && !isQRCodeManager)) {
                  return null;
                }
                
                return (
                  <ListItem key={index} disablePadding>
                    <ListItemButton onClick={() => handleNavigation(item.path)}>
                      <ListItemText primary={item.label} />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
            
            <Divider />
            
            {isAuthenticated && (
              <Box sx={{ p: 2 }}>
                <Button 
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  fullWidth
                >
                  Logout
                </Button>
              </Box>
            )}
          </Drawer>          {/* Desktop Auth Buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isAuthenticated ? (
              <>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    display: { xs: 'none', sm: 'block' },
                  }}
                >
                  Hello, {volunteer?.name}
                </Typography>
                <Button 
                  color="inherit"
                  variant="outlined"
                  onClick={logout}
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    },
                    // Always show logout button (even on mobile)
                    display: 'block'
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <Button
                component={RouterLink}
                to="/login"
                sx={{
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Header;
